import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Phone, Mail, Calendar as CalendarIcon, CreditCard } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interestOptions = [
  "Yoga Therapy — Assessment",
  "Yoga Therapy — Package",
  "60-min Session",
  "90-min Session",
  "Astrology Integration",
  "General Question",
];

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please share your name, email, and a brief message.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        interest: form.interest || null,
        message: form.message.trim(),
      };
      await axios.post(`${API}/contact`, payload);
      toast.success("Thank you. Marissa will reach out personally.");
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or call directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      {/* Header */}
      <section className="pt-40 pb-16 bg-[#FDFCFB]">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="text-xs tracking-[0.35em] uppercase text-[#7B8E76] mb-6">
            Contact & Booking
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#2D3A2B] leading-[1.05] tracking-tight max-w-4xl">
            Personal care begins with a{" "}
            <span className="italic text-[#4A5D46]">personal conversation</span>.
          </h1>
          <p className="mt-8 text-lg text-[#4A5D46] leading-relaxed max-w-3xl font-light">
            To ensure the highest quality of personalized care, all bookings are
            currently done directly with Marissa. Please call, text, or email to
            set up your assessment and schedule via Google Calendar.
          </p>
        </div>
      </section>

      {/* Contact grid */}
      <section className="pb-24 md:pb-32 bg-[#FDFCFB]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          {/* Left — details */}
          <div className="md:col-span-5 space-y-8">
            <div
              data-testid="contact-info-phone"
              className="p-6 bg-[#F4F1ED] rounded-md border border-[#E8E5DF]"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="w-10 h-10 rounded-full bg-[#8A9A83] text-white flex items-center justify-center">
                  <Phone className="w-4 h-4" aria-hidden />
                </span>
                <p className="text-xs tracking-widest uppercase text-[#7B8E76]">
                  Call or Text
                </p>
              </div>
              <a
                href="tel:+12048990166"
                className="font-serif text-3xl text-[#2D3A2B] hover:text-[#4A5D46]"
                data-testid="contact-phone-link"
              >
                204-899-0166
              </a>
            </div>

            <div
              data-testid="contact-info-email"
              className="p-6 bg-[#F4F1ED] rounded-md border border-[#E8E5DF]"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="w-10 h-10 rounded-full bg-[#8A9A83] text-white flex items-center justify-center">
                  <Mail className="w-4 h-4" aria-hidden />
                </span>
                <p className="text-xs tracking-widest uppercase text-[#7B8E76]">
                  Email
                </p>
              </div>
              <a
                href="mailto:shaktiyogini.marketing@gmail.com"
                className="font-serif text-xl md:text-2xl text-[#2D3A2B] hover:text-[#4A5D46] break-all"
                data-testid="contact-email-link"
              >
                shaktiyogini.marketing@gmail.com
              </a>
            </div>

            <div
              data-testid="contact-info-payments"
              className="p-6 bg-[#2D3A2B] rounded-md text-[#FDFCFB]"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="w-10 h-10 rounded-full bg-[#C6A692] text-[#2D3A2B] flex items-center justify-center">
                  <CreditCard className="w-4 h-4" aria-hidden />
                </span>
                <p className="text-xs tracking-widest uppercase text-[#C6A692]">
                  Payment Terms
                </p>
              </div>
              <p className="text-[#C6BFB4] leading-relaxed font-light">
                Invoices are securely processed via <strong>Wave</strong>.
                Payments are accepted via E-transfer, Cash, or Credit Card.
              </p>
              <p className="mt-3 text-xs italic text-[#8A9A83]">
                Please note: a 2% convenience fee applies to all credit card
                transactions.
              </p>
            </div>
          </div>

          {/* Right — form */}
          <div className="md:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="bg-[#F4F1ED] p-8 md:p-10 rounded-md border border-[#E8E5DF]"
              noValidate
            >
              <h2 className="font-serif text-3xl text-[#2D3A2B] mb-2">
                Send a note
              </h2>
              <p className="text-sm text-[#4A5D46] mb-8 font-light">
                Share a little about what brings you here. Marissa will respond
                personally.
              </p>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-[#2D3A2B]">
                    Full Name <span className="text-[#C6A692]">*</span>
                  </Label>
                  <Input
                    id="name"
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    className="mt-2 bg-white border-[#E8E5DF]"
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[#2D3A2B]">
                    Email <span className="text-[#C6A692]">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className="mt-2 bg-white border-[#E8E5DF]"
                    placeholder="you@email.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[#2D3A2B]">
                    Phone (optional)
                  </Label>
                  <Input
                    id="phone"
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    className="mt-2 bg-white border-[#E8E5DF]"
                    placeholder="Preferred call/text number"
                  />
                </div>
                <div>
                  <Label className="text-[#2D3A2B]">Interest</Label>
                  <Select
                    value={form.interest}
                    onValueChange={(v) => update("interest", v)}
                  >
                    <SelectTrigger
                      data-testid="contact-select-interest"
                      className="mt-2 bg-white border-[#E8E5DF]"
                    >
                      <SelectValue placeholder="Select an area of interest" />
                    </SelectTrigger>
                    <SelectContent>
                      {interestOptions.map((o) => (
                        <SelectItem
                          key={o}
                          value={o}
                          data-testid={`interest-option-${o.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-5">
                <Label htmlFor="message" className="text-[#2D3A2B]">
                  Your message <span className="text-[#C6A692]">*</span>
                </Label>
                <Textarea
                  id="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  rows={6}
                  className="mt-2 bg-white border-[#E8E5DF]"
                  placeholder="Share a little about your goals, symptoms, or questions."
                  required
                />
              </div>

              <div className="mt-8 flex items-center justify-between gap-4">
                <p className="text-xs text-[#7B8E76] italic max-w-xs">
                  Your information is kept private and used only to respond to
                  your inquiry.
                </p>
                <Button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="rounded-full bg-[#8A9A83] hover:bg-[#7B8E76] text-white px-8 py-5 h-auto"
                >
                  {submitting ? "Sending..." : "Send Inquiry"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Google Calendar */}
      <section
        data-testid="contact-calendar"
        className="pb-24 md:pb-32 bg-[#FDFCFB]"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-10 rounded-full bg-[#C6A692] text-[#2D3A2B] flex items-center justify-center">
              <CalendarIcon className="w-4 h-4" aria-hidden />
            </span>
            <p className="text-xs tracking-[0.35em] uppercase text-[#7B8E76]">
              Schedule via Google Calendar
            </p>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#2D3A2B] leading-tight mb-6 max-w-2xl">
            Once we&rsquo;ve connected, we&rsquo;ll schedule your assessment together.
          </h2>
          <div className="bg-[#F4F1ED] border border-[#E8E5DF] rounded-md p-6 md:p-8">
            <p className="text-[#4A5D46] leading-relaxed font-light mb-4">
              Marissa will send you a personal Google Calendar link once you
              reach out. In the meantime, you can view her general availability
              by requesting access below.
            </p>
            <div className="aspect-video w-full bg-[#FDFCFB] border border-[#E8E5DF] rounded-md flex items-center justify-center text-center p-8">
              <div>
                <CalendarIcon
                  className="w-10 h-10 text-[#8A9A83] mx-auto mb-4"
                  aria-hidden
                />
                <p className="font-serif text-2xl text-[#2D3A2B] mb-2">
                  Personal Google Calendar Booking
                </p>
                <p className="text-sm text-[#4A5D46] font-light max-w-md mx-auto">
                  Your personalized scheduling link will be shared after your
                  first outreach — ensuring the highest quality of individual
                  care.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
